/**
 * Customer Studio record → its deeplink route, plus the per-record CONTROLS the
 * agent may point at but never press. Separate from `masterDataAgentTargets`:
 * neither entity is master data. Routes are imported from `routes.ts`, never
 * rebuilt here.
 *
 * The controls are deliberately NOT entries in the shared `agentTargets` catalog,
 * for the same reason `declarationReviewAgentTargets` is not: that catalog keys a
 * target by a fixed route, and every control below lives on ONE request's or ONE
 * declaration's screen. The route is built per action; the selector is attached
 * to it here.
 *
 * Each control is marked in the DOM with `data-agent-target="<key>"`, the one
 * convention `targetSelector` reads.
 */
import {
  targetSelector,
  type AgentTarget,
} from "@/features/agent-chat/actions/agentTargets"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import {
  conformityEditPath,
  customerRequestDetailPath,
  customerRequestProcessPath,
} from "./routes"

export const CUSTOMER_STUDIO_ENTITIES = [
  "customer_request",
  "conformity_declaration",
] as const

export type CustomerStudioEntity = (typeof CUSTOMER_STUDIO_ENTITIES)[number]

export function isCustomerStudioEntity(
  value: unknown,
): value is CustomerStudioEntity {
  return (
    typeof value === "string" &&
    (CUSTOMER_STUDIO_ENTITIES as readonly string[]).includes(value)
  )
}

export function customerStudioRecordRoute(
  entity: CustomerStudioEntity,
  recordId: string,
): string {
  if (entity === "customer_request") {
    return customerRequestDetailPath(recordId)
  }
  // Same route the inbox row click uses.
  return conformityEditPath(recordId)
}

/**
 * Every screen here mounts its controls only once the record's own fetch has
 * resolved — the editor renders an empty shell until `useConformityDetail`
 * lands, and a wizard step until its own read does. So none of these is in the
 * DOM on arrival, and the default wait expires first, reporting the control as
 * missing when it was merely not there yet.
 */
const CONTROL_WAIT_MS = 10_000

// ── The conformity editor's two hand-over buttons ────────────────────────────

export const CONFORMITY_CONTROLS = ["approve", "send"] as const

export type ConformityControl = (typeof CONFORMITY_CONTROLS)[number]

const CONFORMITY_CONTROL_KEYS: Record<
  ConformityControl,
  { key: string; labelKey: TranslationKeys }
> = {
  approve: {
    key: "conformity.approve",
    labelKey: "AI_CHAT/TARGET_LABEL_CONFORMITY_APPROVE",
  },
  send: {
    key: "conformity.send",
    labelKey: "AI_CHAT/TARGET_LABEL_CONFORMITY_SEND",
  },
}

export function isConformityControl(
  value: unknown,
): value is ConformityControl {
  return (
    typeof value === "string" &&
    (CONFORMITY_CONTROLS as readonly string[]).includes(value)
  )
}

export function conformityControlTarget(
  control: ConformityControl,
  route: string,
): AgentTarget {
  const descriptor = CONFORMITY_CONTROL_KEYS[control]
  return {
    route,
    selector: targetSelector(descriptor.key),
    labelKey: descriptor.labelKey,
    waitTimeoutMs: CONTROL_WAIT_MS,
  }
}

// ── The intake wizard's stages and their controls ────────────────────────────

export const CUSTOMER_REQUEST_STEPS = [
  "mapping",
  "reconcile",
  "gaps",
  "review",
] as const

export type CustomerRequestStep = (typeof CUSTOMER_REQUEST_STEPS)[number]

export const CUSTOMER_REQUEST_CONTROLS = [
  "confirm_mapping",
  "confirm_matches",
  "reopen_for_edits",
  "download_response",
] as const

export type CustomerRequestControl = (typeof CUSTOMER_REQUEST_CONTROLS)[number]

const REQUEST_CONTROL_KEYS: Record<
  CustomerRequestControl,
  { key: string; labelKey: TranslationKeys }
> = {
  confirm_mapping: {
    key: "request.confirm-mapping",
    labelKey: "AI_CHAT/TARGET_LABEL_REQUEST_CONFIRM_MAPPING",
  },
  confirm_matches: {
    key: "request.confirm-matches",
    labelKey: "AI_CHAT/TARGET_LABEL_REQUEST_CONFIRM_MATCHES",
  },
  reopen_for_edits: {
    key: "request.reopen",
    labelKey: "AI_CHAT/TARGET_LABEL_REQUEST_REOPEN",
  },
  download_response: {
    key: "request.download",
    labelKey: "AI_CHAT/TARGET_LABEL_REQUEST_DOWNLOAD",
  },
}

export function isCustomerRequestStep(
  value: unknown,
): value is CustomerRequestStep {
  return (
    typeof value === "string" &&
    (CUSTOMER_REQUEST_STEPS as readonly string[]).includes(value)
  )
}

export function isCustomerRequestControl(
  value: unknown,
): value is CustomerRequestControl {
  return (
    typeof value === "string" &&
    (CUSTOMER_REQUEST_CONTROLS as readonly string[]).includes(value)
  )
}

export function customerRequestStepRoute(
  requestId: string,
  step: CustomerRequestStep,
): string {
  return customerRequestProcessPath(requestId, step)
}

export function customerRequestControlTarget(
  control: CustomerRequestControl,
  route: string,
): AgentTarget {
  const descriptor = REQUEST_CONTROL_KEYS[control]
  return {
    route,
    selector: targetSelector(descriptor.key),
    labelKey: descriptor.labelKey,
    waitTimeoutMs: CONTROL_WAIT_MS,
  }
}
